import React, { useEffect, useState } from 'react';
import { Container, Header, Segment } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CreateProgrammingRequest } from '../../../../models/programming';
import { useBlockUI } from '../../../../shared/services/block-ui-service';
import { ToasterConfig } from '../../../../config/ToasterConfig';
import useProgrammingService from '../../../../services/backoffice/hooks/useProgrammingService';
import { ShortDto } from '../../../../models/short-dto';
import ProgrammingForm from '../components/ProgrammingForm';

// Services for dropdown data
import useClientService from '../../../../services/backoffice/hooks/useClientService';
import useVehiclePlateService from '../../../../services/backoffice/hooks/useVehiclePlateService';
import useProductsService from '../../../../services/backoffice/hooks/useProductsService';
import useDriverService from '../../../../services/backoffice/hooks/useDriverService';
import useReferenceService from '../../../../services/backoffice/hooks/useReferenceService';
import useReferenceGroupService from '../../../../services/backoffice/hooks/useReferenceGroupService';
import useRouteService from "../../../../services/backoffice/hooks/useRouteService";

const CreateProgrammingPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const navigate = useNavigate();
    const blockUI = useBlockUI();
    const { createProgramming } = useProgrammingService();

    // Dropdown data
    const [clients, setClients] = useState<ShortDto[]>([]);
    const [vehiclePlates, setVehiclePlates] = useState<ShortDto[]>([]);
    const [products, setProducts] = useState<ShortDto[]>([]);
    const [drivers, setDrivers] = useState<ShortDto[]>([]);
    const [references, setReferences] = useState<ShortDto[]>([]);
    const [routes, setRoutes] = useState<ShortDto[]>([]);

    // Services
    const clientService = useClientService();
    const vehiclePlateService = useVehiclePlateService();
    const productsService = useProductsService();
    const driverService = useDriverService();
    const referenceService = useReferenceService();
    const routeService = useRouteService();

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                // Carregar dados para os dropdowns
                const clientsResponse = await clientService.getAllClientsShortDto();
                setClients(clientsResponse || []);
                
                const vehiclePlatesResponse = await vehiclePlateService.getAllVehiclePlatesShortDto();
                setVehiclePlates(vehiclePlatesResponse || []);
                
                const productsResponse = await productsService.getProductsShort();
                setProducts(productsResponse || []);
                
                const driversResponse = await driverService.getAllDriversShortDto();
                setDrivers(driversResponse || []);
                
                const referencesResponse = await referenceService.getReferencesShort();
                setReferences(referencesResponse || []);
                
                const routesResponse = await routeService.getShortRoutes();
                setRoutes(routesResponse || []);
                
            } catch (error: any) {
                toast.error('Erro ao carregar dados para formulário', ToasterConfig.error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, []);

    const handleSubmit = async (formData: CreateProgrammingRequest | any) => {
        try {
            setLoading(true);
            // Garantindo que os dados sejam do tipo CreateProgrammingRequest
            const createData: CreateProgrammingRequest = {
                clientId: formData.clientId,
                mainVehiclePlateId: formData.mainVehiclePlateId,
                semiTrailer1Id: formData.semiTrailer1Id,
                semiTrailer2Id: formData.semiTrailer2Id,
                volume: formData.volume,
                productId: formData.productId,
                driverId: formData.driverId,
                travelType: formData.travelType,
                routeId: formData.routeId,
                originReferenceId: formData.originReferenceId,
                destinationReferenceId: formData.destinationReferenceId,
                expectedDepartureTime: formData.expectedOriginDepartureAt ?? formData.expectedDepartureTime,
                expectedArrivalTime: formData.expectedDestinationArrivalAt ?? formData.expectedArrivalTime,
                expectedOriginDepartureAt: formData.expectedOriginDepartureAt,
                expectedDestinationArrivalAt: formData.expectedDestinationArrivalAt,
                expectedDestinationDepartureAt: formData.expectedDestinationDepartureAt,
                expectedReturnArrivalAt: formData.expectedReturnArrivalAt,
                isGoAndReturn: formData.isGoAndReturn ?? formData.isReturn ?? false,
                isReturn: formData.isGoAndReturn ?? formData.isReturn ?? false,
                returnDestinationReferenceId: formData.returnDestinationReferenceId,
                distanceKm: formData.distanceKm
            };
            
            await createProgramming(createData);
            toast.success('Programação criada com sucesso', ToasterConfig.success);
            navigate('/backoffice/programming');
        } catch (error: any) {
            toast.error('Erro ao criar programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/backoffice/programming');
    };

    return (
        <Container>
            <Header as="h3" textAlign="center" color="green">
                Nova Programação
            </Header>
            <Segment>
                <ProgrammingForm
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    loading={loading}
                    error={error}
                    clients={clients}
                    vehiclePlates={vehiclePlates}
                    products={products}
                    drivers={drivers}
                    references={references}
                    routes={routes}
                />
            </Segment>
        </Container>
    );
};

export default CreateProgrammingPage; 